/* eslint-disable no-nested-ternary */
/* eslint-disable @typescript-eslint/no-explicit-any */
import IconClose from '@assets/icons/close.svg?react';
import IconPlus from '@assets/icons/plus.svg?react';
import IconBigArrowLeft from '@assets/icons/big arrow left.svg?react';
import IconBigArrowRight from '@assets/icons/big arrow right.svg?react';
import IconSave from '@assets/icons/save.svg?react';
import {
 AppBar,
 Box,
 Button,
 CircularProgress,
 Dialog,
 IconButton,
 Menu,
 MenuItem,
 Slide,
 Theme,
 Toolbar,
 Typography,
 useTheme,
} from '@mui/material';
import dayjs from 'dayjs';
import { TransitionProps } from 'notistack';
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';

import { useConfirmationDialog } from '../../../contexts/useConfirmationDialog';
import { usePage } from '../../../contexts/usePageContext';
import { useAppDispatch } from '../../../redux/hooks';
import {
 getResultPicturesStart,
 ResultPicture,
} from '../../../redux/results/resultsSlice';
import { RootState } from '../../../redux/store';
import {
 deleteWeightingPictureStart,
 uploadWeightingPicturesStart,
} from '../../../redux/weightings/weightingsSlice';
import { createDataUrl } from '../../../utils/base64File';
import { titleStyles } from '../../common/InfoSidebar/infoStyles';
import LazySvg from '../../common/LazySvg';
import RoleChecker from '../../common/RoleChecker';

const Transition = React.forwardRef(
 (
  props: TransitionProps & {
   children: React.ReactElement<unknown>;
  },
  ref: React.Ref<unknown>,
 ) => <Slide direction="up" ref={ref} {...props} />,
);
Transition.displayName = 'Transition';

const PictureModal = ({
 open,
 handleOpen,
 handleDelete,
 pictures,
 currentIndex,
 onPrevious,
 onNext,
 pageName,
}: {
 open: boolean;
 handleOpen: (is: boolean) => void;
 handleDelete: () => void;
 pictures: ResultPicture[];
 currentIndex: number;
 onPrevious: () => void;
 onNext: () => void;
 pageName: string;
}) => {
 const { t } = useTranslation();
 const th = useTheme();
 const picture = pictures[currentIndex];
 const hasPrevious = currentIndex > 0;
 const hasNext = currentIndex < pictures.length - 1;
 const pictureDataUrl = createDataUrl(picture?.PicData, picture?.Type);
 const pictureDownloadName = `${picture?.Id}.${picture?.Type?.toLowerCase()}`;

 const handleKeyDown = (event: React.KeyboardEvent) => {
  if (event.key === 'ArrowLeft' && hasPrevious) {
   onPrevious();
  } else if (event.key === 'ArrowRight' && hasNext) {
   onNext();
  } else if (event.key === 'Escape') {
   handleOpen(false);
  }
 };

 return (
  <Dialog
   fullScreen
   open={open}
   onClose={() => handleOpen(false)}
   TransitionComponent={Transition}
   onKeyDown={handleKeyDown}
   tabIndex={0}
  >
   <AppBar
    sx={{
     position: 'relative',
     background: (theme: Theme) => theme.palette.kemek.white,
     boxShadow: 'none',
    }}
   >
    <Toolbar sx={{ display: 'flex', alignItems: 'center' }}>
     <Box
      sx={{
       display: 'flex',
       alignItems: 'center',
       width: '100%',
       gap: 5,
       fontSize: 18,
      }}
     >
      {/* <Typography sx={{ fontSize: 'inherit' }}>
       {t('global.pictureTypeInfo')}
      </Typography> */}
      <Box sx={{ display: 'flex', gap: 2 }}>
       <Typography
        sx={{
         fontSize: 'inherit',
         flex: 1,
         color: (theme: Theme) => theme.palette.kemek.gray,
         whiteSpace: 'nowrap',
        }}
       >
        {dayjs(picture?.PicWhen).format('YYYY-MM-DD')}
       </Typography>
       <Typography sx={{ fontSize: 'inherit', whiteSpace: 'nowrap' }}>
        {dayjs(picture?.PicWhen).format('HH:mm:ss')}
       </Typography>
      </Box>
      <Typography
       sx={{
        fontSize: 'inherit',
        whiteSpace: 'nowrap',
        color: (theme: Theme) => theme.palette.kemek.gray,
       }}
      >
       {currentIndex + 1}/{pictures.length}
      </Typography>
      <Box sx={{ display: 'flex', gap: 2, ml: 'auto', mr: 'auto' }}>
       <RoleChecker resource={pageName} action="DeletePictures">
        <Button
         variant="outlined"
         color="error"
         sx={{ height: 40 }}
         endIcon={
          <LazySvg
           name="delete"
           styles={{
            '& path': { stroke: (theme: Theme) => theme.palette.kemek.red },
           }}
          />
         }
         onClick={() => handleDelete()}
        >
         {t('global.delete')}
        </Button>
       </RoleChecker>
       <Button
        component="a"
        variant="outlined"
        sx={{ height: 40 }}
        href={pictureDataUrl}
        download={pictureDownloadName}
        endIcon={<IconSave />}
       >
        {t('global.download')}
       </Button>
      </Box>
     </Box>
     <IconButton
      sx={{}}
      edge="start"
      color="inherit"
      onClick={() => {
       handleOpen(false);
      }}
      aria-label={t('global.toggleImageAriaLabel')}
     >
      <IconClose
       style={{
        width: 20,
        height: 20,
        color: th.palette.kemek.darkestGray,
       }}
      />
     </IconButton>
    </Toolbar>
   </AppBar>
   <Box
    sx={{
     display: 'flex',
     justifyContent: 'center',
     alignItems: 'center',
     maxHeight: 'calc(100% - 64px)',
     width: '100%',
     background: th.palette.kemek.gray,
     flex: 1,
     position: 'relative',
    }}
   >
    {hasPrevious && (
     <IconButton
      onClick={onPrevious}
      sx={{
       position: 'absolute',
       left: 16,
       zIndex: 1,
       backgroundColor: 'rgba(255, 255, 255, 0.8)',
       '&:hover': {
        backgroundColor: 'rgba(255, 255, 255, 0.9)',
       },
      }}
      aria-label={t('global.previousImageAriaLabel')}
     >
      <IconBigArrowLeft
       style={{
        width: 24,
        height: 24,
        color: th.palette.kemek.darkestGray,
       }}
      />
     </IconButton>
    )}
    {hasNext && (
     <IconButton
      onClick={onNext}
      sx={{
       position: 'absolute',
       right: 16,
       zIndex: 1,
       backgroundColor: 'rgba(255, 255, 255, 0.8)',
       '&:hover': {
        backgroundColor: 'rgba(255, 255, 255, 0.9)',
       },
      }}
      aria-label={t('global.nextImageAriaLabel')}
     >
      <IconBigArrowRight
       style={{
        width: 24,
        height: 24,
        color: th.palette.kemek.darkestGray,
       }}
      />
     </IconButton>
    )}
    <Link
     to={pictureDataUrl}
     target="_blank"
     style={{
      display: 'block',
      height: '100%',
      maxWidth: '100%',
     }}
     download={pictureDownloadName}
    >
     <img
      src={pictureDataUrl}
      alt={t('global.imageAlt')}
      style={{ display: 'block', height: '100%' }}
     />
    </Link>
   </Box>
  </Dialog>
 );
};

const PictureBox = ({
 picture,
 small,
 onClick,
 overlayCount,
}: {
 picture: ResultPicture;
 small?: boolean;
 onClick: () => void;
 overlayCount?: number;
}) => {
 const blobUrl = useCallback(
  () => createDataUrl(picture?.Thumbnail, picture?.Type),
  [picture],
 )();

 return (
  <Box
   sx={{
    position: 'relative',
    width: '100%',
    height: small ? '54px' : '177px',
    borderRadius: '4px',
    backgroundImage: `url(${blobUrl})`,
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    backgroundRepeat: 'no-repeat',
    cursor: 'pointer',
    overflow: 'hidden',
   }}
   onClick={onClick}
  >
   {overlayCount && overlayCount > 0 ? (
    <Box
     sx={{
      position: 'absolute',
      inset: 0,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: 'rgba(0, 0, 0, 0.55)',
      color: (theme: Theme) => theme.palette.kemek.white,
      fontSize: small ? 14 : 20,
      fontWeight: 600,
      borderRadius: '4px',
     }}
    >
     +{overlayCount}
    </Box>
   ) : null}
  </Box>
 );
};

const InfoImages = ({ data }: { data: any }) => {
 const { t } = useTranslation();
 const dispatch = useAppDispatch();
 const { loading, success, pictures } = useSelector(
  (state: RootState) => state.results,
 );
 const {
  loading: weightingLoading,
  success: weightingSuccess,
  weightingPictures,
 } = useSelector((state: RootState) => state.weightings);
 const th = useTheme();
 const { openDialog: openConfirmationDialog } = useConfirmationDialog();
 const { pageName } = usePage();

 const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
 const [modalOpen, setModalOpen] = useState(false);
 const [currentIndex, setCurrentIndex] = useState(0);

 const handlePicturesUpload = async (weightingId: number) => {
  const input = document.createElement('input');
  input.type = 'file';
  input.multiple = true;
  input.accept = 'image/*';
  input.onchange = (event) => {
   const { files } = event.target as HTMLInputElement;

   if (files) {
    dispatch(
     uploadWeightingPicturesStart({
      weighingId: weightingId,
      files,
     }),
    );
   }
  };
  input.click();
 };

 const handleOpenModal = (index: number) => {
  setCurrentIndex(index);
  setModalOpen(true);
 };

 const handleCloseModal = () => {
  setModalOpen(false);
 };

 const handlePrevious = () => {
  if (currentIndex > 0) {
   setCurrentIndex(currentIndex - 1);
  }
 };

 const handleNext = () => {
  if (pictures && currentIndex < pictures.length - 1) {
   setCurrentIndex(currentIndex + 1);
  }
 };

 const handleDelete = () => {
  if (!pictures || !pictures[currentIndex]) return;
  const picture = pictures[currentIndex];
  openConfirmationDialog({
   title: t('global.delete'),
   message: t('global.deleteMessage'),
   onConfirm: () => {
    dispatch(deleteWeightingPictureStart(picture.Id));
    if (pictures.length === 1) {
     setModalOpen(false);
    } else if (currentIndex === pictures.length - 1) {
     setCurrentIndex(currentIndex - 1);
    }
   },
  });
 };

 useEffect(() => {
  // console.log('data?.Id', data?.Id);
  if (data?.Id) {
   dispatch(getResultPicturesStart(data?.Id));
  }
 }, [data?.Id]);

 useEffect(() => {
  if (weightingSuccess?.uploadWeightingPictures) {
   dispatch(getResultPicturesStart(data?.Id));
  }
 }, [weightingSuccess]);

 return (
  <RoleChecker resource={pageName} action="GetPictures">
   <Box sx={{ mb: '20px' }}>
    <Typography sx={{ ...titleStyles }}>
     {t('global.pictures')} {/* future improvement */}
     {/* <IconButton
     id="result-sidebar-image-menu-button"
     aria-controls={anchorEl ? 'result-sidebar-image-menu' : undefined}
     aria-haspopup="true"
     aria-expanded={anchorEl ? 'true' : undefined}
     onClick={(e: React.MouseEvent<HTMLButtonElement>) =>
      setAnchorEl(e.currentTarget)
     }
     //  onClick={() => {
     //   handlePicturesUpload();
     //  }}
    >
     <IconPlus style={{ color: th.palette.kemek.darkestGray }} />
    </IconButton>
    <Menu
     id="result-sidebar-image-menu"
     MenuListProps={{
      'aria-labelledby': 'result-sidebar-image-menu-button',
      disablePadding: true,
     }}
     anchorEl={anchorEl}
     open={!!anchorEl}
     onClose={() => setAnchorEl(null)}
     //  anchorOrigin={{
     //   vertical: 'bottom',
     //   horizontal: 'right',
     //  }}
     //  transformOrigin={{
     //   vertical: 'top',
     //   horizontal: 'right',
     //  }}
    >
     <MenuItem onClick={() => handlePicturesUpload(data?.Bruto?.Id)}>
      <Typography variant="inherit">
       {t('results.upload')} {t('results.bruto')}
      </Typography>
     </MenuItem>
     <MenuItem onClick={() => handlePicturesUpload(data?.Tare?.Id)}>
      <Typography variant="inherit">
       {t('results.upload')} {t('results.tare')}
      </Typography>
     </MenuItem>
    </Menu> */}
    </Typography>
    {loading?.getResultPictures ? (
     <CircularProgress />
    ) : pictures?.length ? (
     <Box
      sx={{
       display: 'flex',
       alignItems: 'flex-start',
       gap: 1,
      }}
     >
      <Box sx={{ width: '64%' }}>
       <PictureBox
        key={pictures[0]?.Id}
        picture={pictures[0]}
        onClick={() => handleOpenModal(0)}
       />
      </Box>
      <Box
       sx={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
        alignItems: 'flex-start',
       }}
      >
       {pictures
        ?.slice(1, 4)
        .map((picture: ResultPicture, index: number, arr: ResultPicture[]) => {
         const isLastVisible = index === arr.length - 1;
         const hiddenCount = pictures.length - 4;
         const showOverlay = isLastVisible && hiddenCount > 0;

         return (
          <PictureBox
           key={picture.Id}
           picture={picture}
           small
           onClick={() => handleOpenModal(index + 1)}
           overlayCount={showOverlay ? hiddenCount : undefined}
          />
         );
        })}
      </Box>
     </Box>
    ) : (
     <Typography variant="inherit">{t('global.noPictures')}</Typography>
    )}
    {pictures && pictures.length > 0 && (
     <PictureModal
      open={modalOpen}
      handleOpen={handleCloseModal}
      handleDelete={handleDelete}
      pictures={pictures}
      currentIndex={currentIndex}
      onPrevious={handlePrevious}
      onNext={handleNext}
      pageName={pageName}
     />
    )}
   </Box>
  </RoleChecker>
 );
};
export default InfoImages;
